import React from "react";
import { Leaf, ShieldCheck, Video, DoorOpen, Car, Bus, CreditCard, Sparkles, Users, Heart, User, Bike } from "lucide-react";
import {
  CAMPFIRE_IMAGE,
  SPYZZZ_LOGO,
  SAFETY_POINTS,
  CLEAN_FEATURES,
  CLEAN_IMAGES,
  SUPPORT_POINTS,
  HOSTS_IMAGE,
  NEARBY_PLACES,
} from "../mock";

export const About = () => (
  <section id="about" className="py-20 md:py-28 bg-white">
    <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
      <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#17a89b] mb-4">
        Auracrest Haven Munnar
      </p>
      <h2 className="text-3xl md:text-5xl font-extrabold text-neutral-900 leading-tight mb-8">
        Nestled in Nature.<br />Rooted in Serenity.
      </h2>
      <p className="text-neutral-600 leading-8 text-[15px] md:text-base">
        Imagine waking up to the sweet melodies of cuckoos and being surrounded by the vibrant
        calls of parrots and other birds, all within a tranquil, natural setting amidst the misty
        hills. At Auracrest Haven, a luxury homestay near Sengulam Dam in Munnar, the rippling and
        gurgling of a flowing stream beneath the rooms lulls you into serenity. Surrounded by lush
        cardamom and nutmeg plantations, our property is nestled on the ridges of a small hillock,
        providing breathtaking views and a serene ambience that’s hard to find in the modern world.
      </p>
    </div>
  </section>
);

export const SeoContent = () => (
  <section className="pb-8 md:pb-14 bg-white">
    <div className="max-w-5xl mx-auto px-5 md:px-8">
      <div className="bg-neutral-50 rounded-3xl p-8 md:p-12">
        <p className="text-lg md:text-xl italic text-neutral-500 leading-8 mb-6">
          Wake to birdsong, fall asleep to a gurgling stream — surrounded by cardamom and nutmeg
          plantations on the misty ridges of Sengulam.
        </p>
        <h2 className="text-2xl md:text-3xl font-extrabold text-neutral-900 mb-5">
          The Best Stay &amp; Accommodation in Munnar
        </h2>
        <p className="text-neutral-600 leading-8 mb-8">
          Auracrest Haven offers premium <strong>accommodation in Munnar</strong> that blends the
          charm of a boutique resort with the warmth of a private homestay. Set near{" "}
          <strong>Sengulam Dam</strong> in <strong>Anachal, Muthuvankudy, Idukki, Kerala</strong>,
          our plush rooms are ideal for families, couples on a honeymoon, solo travellers, bikers,
          friends and tourist groups seeking serenity amid the misty hills. With homemade food for
          all three meals, a free 24-hour self-service tea-coffee lounge, free spacious concrete
          parking, 24-hour power, hot water and filter water — and an owner family always on-site —
          Auracrest Haven is the safest and most welcoming place to stay in Munnar.
        </p>

        <div className="grid sm:grid-cols-2 gap-5 mb-8">
          {[
            {
              icon: Users,
              title: "Families",
              desc: "Spacious rooms, CCTV security and a caring owner family on-site for every need — the safest stay in Munnar",
            },
            {
              icon: Heart,
              title: "Couples & Honeymooners",
              desc: "Peaceful hilltop seclusion with forest views, homemade meals and a 24-hour tea-coffee lounge",
            },
            {
              icon: User,
              title: "Solo Travellers",
              desc: "A safe, welcoming base with 24-hour power, hot water, filter water and owner family always available",
            },
            {
              icon: Bike,
              title: "Bikers, Friends & Groups",
              desc: "Free spacious concrete parking, easy access to all Munnar attractions and homemade food for all three meals",
            },
          ].map((p) => (
            <div key={p.title} className="bg-white rounded-2xl p-6 shadow-sm flex gap-4">
              <div className="w-11 h-11 shrink-0 rounded-xl bg-[#e3faf7] flex items-center justify-center">
                <p.icon className="w-5 h-5 text-[#17a89b]" />
              </div>
              <div>
                <h3 className="text-base font-bold text-neutral-900 mb-1.5">{p.title}</h3>
                <p className="text-neutral-600 leading-7 text-sm">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-neutral-600 leading-8 mb-8">
          From our doorstep you can plan the very best <strong>things to do in Munnar</strong> —
          a thrilling <strong>Zip Line</strong>, relaxing <strong>Boating</strong>, refreshing{" "}
          <strong>Waterfalls</strong>, guided <strong>Sightseeing</strong>, an adventurous{" "}
          <strong>Jeep Safari</strong>, and immersive <strong>Wildlife</strong> and{" "}
          <strong>Jungle Safari</strong> experiences. Wake early for the iconic{" "}
          <strong>Kolukkumalai sunrise</strong>, catch an <strong>Elephant view</strong>, and
          explore tea gardens, spice plantations and the scenic ghat roads that make Munnar
          unforgettable.
        </p>

        <div className="pt-6 border-t border-neutral-200 mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-3">
            Areas we serve near Munnar
          </p>
          <div className="flex flex-wrap gap-2">
            {NEARBY_PLACES.map((t) => (
              <span
                key={t}
                className="text-xs text-neutral-600 bg-white border border-neutral-200 rounded-full px-3 py-1.5"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="pt-6 border-t border-neutral-200">
          <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-3">
            Popular searches
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              "A-frame cottages in Anachal Munnar",
              "Luxury homestay near Sengulam Dam",
              "Private cottage stay with balcony view in Munnar",
              "Home Stay near Anachal",
              "Home Stay near Sengulam Dam",
              "Stay near Anachal",
              "Accommodation near Anachal",
              "Family Room near Anachal",
              "Couple Room near Anachal",
              "Family Room near Munnar",
              "Couple Room near Munnar",
              "Resort near Anachal",
              "Cottage near Anachal",
              "Resort with food and dining near Anachal",
              "Home Stay with food and dining near Anachal",
              "Family Home Stay with food near Anachal",
            ].map((t) => (
              <span
                key={t}
                className="text-xs text-neutral-600 bg-white border border-neutral-200 rounded-full px-3 py-1.5"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const ScenicDining = () => (
  <section id="dining" className="py-14 md:py-20 bg-white">
    <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-8 items-stretch">
      <div className="bg-neutral-50 rounded-3xl p-8 md:p-12 flex flex-col justify-center">
        <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900 mb-3">
          Scenic Dining Amidst Natural Beauty
        </h2>
        <p className="italic text-lg text-neutral-500 mb-6">Eco Oasis</p>
        <p className="text-neutral-600 leading-8 mb-4">
          At Auracrest Haven, our commitment to a clean and eco-friendly environment is unwavering.
          Our scenic restaurant{" "}
          <a
            href="https://www.finediningmunnar.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#17a89b] font-semibold underline underline-offset-2 hover:text-[#128a7f]"
          >
            SpyzzZ@Auracrest Haven
          </a>{" "}
          offers steaming coffee and tea amid nature's beauty for a perfect retreat.
        </p>
        <p className="text-neutral-600">Eco-friendly practices are essential.</p>
      </div>
      <div className="rounded-3xl overflow-hidden min-h-[320px]">
        <img src={CAMPFIRE_IMAGE} alt="Campfire dining" className="w-full h-full object-cover" />
      </div>
    </div>
  </section>
);

export const Spyzzz = () => (
  <section className="py-16 md:py-24 bg-white">
    <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
      <h2 className="text-3xl md:text-5xl font-extrabold text-neutral-900 mb-10">
        Exquisite Dining Experience in Munnar
      </h2>
      <div className="bg-neutral-50 rounded-3xl p-10 md:p-14 shadow-sm">
        <img src={SPYZZZ_LOGO} alt="SpyzzZ" className="h-20 mx-auto mb-6 object-contain" />
        <p className="text-lg font-semibold text-neutral-800 mb-3">Fine Dining in Munnar</p>
        <p className="text-neutral-600 leading-8 mb-8">
          Savor every bite of our delectable dishes while enjoying impeccable service and warm
          hospitality.
        </p>
        <a
          href="https://www.finediningmunnar.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center bg-[#17a89b] hover:bg-[#128a7f] text-white font-semibold px-8 py-3 rounded-md transition-colors"
        >
          Visit SpyzzZ Website
        </a>
      </div>
    </div>
  </section>
);

export const Safety = () => (
  <section id="facilities" className="py-20 md:py-28 bg-neutral-50">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <h2 className="text-3xl md:text-5xl font-extrabold text-center text-neutral-900 mb-4">
        Safety &amp; Freedom in Munnar
      </h2>
      <p className="text-center text-neutral-600 max-w-2xl mx-auto mb-14">
        Our fenced compound provides safety and freedom to explore the lush surroundings comfortably.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {SAFETY_POINTS.map((p, i) => (
          <div key={i} className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-4xl font-extrabold text-[#25d3c4] mb-4">{i + 1}.</div>
            <p className="italic text-neutral-600 leading-7">{p}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Cleanliness = () => {
  const icons = [ShieldCheck, Video, DoorOpen];
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-2 gap-10 items-center mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900 mb-6">
              Hygienic Cleanliness
            </h2>
            <p className="text-neutral-600 leading-8">
              Indulge in delicious, home-cooked meals made with fresh, organic garden ingredients.
              Enjoy filtered water from natural springs retaining essential minerals. Our rooms are
              regularly sanitized for hygiene.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {CLEAN_IMAGES.map((img, i) => (
              <img
                key={i}
                src={img}
                alt="cleanliness"
                className="w-full h-56 object-cover rounded-2xl shadow-sm"
              />
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {CLEAN_FEATURES.map((f, i) => {
            const Icon = icons[i];
            return (
              <div key={i} className="bg-neutral-50 rounded-2xl p-8 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-[#e3faf7] flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-[#17a89b]" />
                </div>
                <h3 className="text-lg font-bold text-neutral-900 mb-3">{f.title}</h3>
                <p className="text-neutral-600 leading-7 text-sm">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const EcoFriendly = () => (
  <section className="py-20 md:py-24 bg-[#0f2e2b]">
    <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
      <div className="w-14 h-14 rounded-2xl bg-[#17a89b]/20 flex items-center justify-center mx-auto mb-6">
        <Leaf className="w-7 h-7 text-[#25d3c4]" />
      </div>
      <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
        Maintaining Eco-Friendly Practices at Auracrest
      </h2>
      <p className="text-neutral-300 leading-8 mb-8">
        We are dedicated to sustainability at Auracrest Haven. Our eco-friendly approach includes
        innovative solutions like bio-digestors, ensuring minimal impact on the environment while
        maintaining a high standard of cleanliness.
      </p>
      <a
        href="#contact"
        className="inline-flex items-center bg-[#25d3c4] hover:bg-[#1cc0b2] text-[#0f2e2b] font-semibold px-8 py-3 rounded-md transition-colors"
      >
        Get in Touch
      </a>
    </div>
  </section>
);

export const Support = () => {
  const icons = [Car, Bus, CreditCard, Sparkles];
  return (
    <section className="py-20 md:py-28 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <h2 className="text-3xl md:text-5xl font-extrabold text-center text-neutral-900 mb-4">
          Convenient Support Services
        </h2>
        <p className="text-center text-neutral-600 max-w-2xl mx-auto mb-14">
          We provide Pickup and Drop facility to Kochi Airport and Railway Station for guests.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SUPPORT_POINTS.map((p, i) => {
            const Icon = icons[i];
            return (
              <div key={i} className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-[#e3faf7] flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-[#17a89b]" />
                </div>
                <p className="text-neutral-600 leading-7 text-sm">{p}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Hosts = () => (
  <section className="py-20 md:py-28 bg-white">
    <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 items-center">
      <div className="order-2 md:order-1">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#17a89b] mb-4">
          Your Hosts
        </p>
        <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900 mb-6">Home Welcomers</h2>
        <p className="text-neutral-600 leading-8">
          Anand (Husband) and Lisha (Wife), the proud owners of Auracrest Haven, reside in the
          adjoining house. With roots deeply connected to Munnar, Anand's family has a rich history
          in the region, having operated a bus service from Munnar to Ernakulam during 1960 to 1980.
          After marrying in 1998, the couple spent years living abroad and in various cities,
          including Dubai, Bangalore, and Kochi. Driven by their passion for hospitality, they
          ventured into the homestay business, welcoming guests to Auracrest Haven.
        </p>
      </div>
      <div className="order-1 md:order-2">
        <img src={HOSTS_IMAGE} alt="Anand and Lisha, hosts of Auracrest Haven" className="w-full h-[520px] object-cover object-[center_60%] rounded-3xl shadow-lg" />
      </div>
    </div>
  </section>
);
